import pick from 'lodash/pick';
import type {
  CellClassParams,
  ColDef,
  Column,
  GridApi,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ColGroupDef,
} from 'ag-grid-community';

import type { ScenarioDetailRow } from '@queries/scenarios';
import type { UserNumberFormatterOptions } from '../../../shared/useUserNumberFormatterOptions.hook';
import { alignLeftCellStyle, alignRightCellStyle } from '@common/cellStyles';
import { GridMode, isEditingMode, RowWithMetadata, SetRowDataFn } from '../../../shared/gridEdit/useEditableGridLogic';
import { customNumberFormatter } from '../../../shared/valueFormatters/customNumberFormatter';
import {
  adminColumnDefinitionFactory,
  buildEditableGridColumnDefinitions,
  ColumnDefinitionConfig,
} from '../../../shared/gridEdit/columnDefinitionsUtils';
import { floatParser } from '../../../shared/gridEdit/utils/parsers';

import { getDetailKey, getFieldName, getScenarioReportRowDetailId } from './utils';
import { DetailAdminColumnRenderer } from './detailAdminColumnRenderer.component';

import type { ReportColDef } from '../types';
import type {
  DetailColumnDefinition,
  OnRemoveDetailsClick,
  ReportDetailsColumnsContext,
  ScenarioDetailRendererParams,
} from './types';
import { getDetailsSum } from '../../scenarioEditDetails/utils';
import { compareScenariosPrefixes } from '../../shared/constants';
import { CALCULATE_TOTAL_STRATEGY, CALCULATE_TOTAL_TYPES } from '../totalColumn';
import { fieldsToCopyFromParentColumn } from './constants';
import { NoDetailValue } from '../../scenarioEditDetails/constants';


const isGroupColumn = (column: ReportColDef | ColGroupDef): column is ColGroupDef =>
  typeof (column as ColGroupDef).children !== 'undefined';

const commonColumnOptions = {
  resizable: false,
  lockVisible: true,
  suppressMovable: true,
};

const isPinnedCondition = ({ node }: CellClassParams) => node.isRowPinned();
const hasNoDetailValue = ({ value }: CellClassParams) => value === NoDetailValue;

const footerCellClassRule = {
  'font-weight-bold': isPinnedCondition,
};

const noDetailCellClassRule = {
  'font-italic': hasNoDetailValue,
  'text-muted': hasNoDetailValue,
};

export const accountColumnDefinition = {
  headerName: 'Account',
  field: 'account',
  colId: 'account',
  width: 120,
  ...alignLeftCellStyle,
  ...commonColumnOptions,
  pinned: 'left',
};

export const descriptionColumnDefinition = {
  headerName: 'Description',
  field: 'detailName',
  colId: 'detailName',
  ...alignLeftCellStyle,
  resizable: false,
  lockVisible: true,
  suppressMovable: true,
  pinned: 'left',
  cellClassRules: {
    ...footerCellClassRule,
    'text-right': isPinnedCondition,
  },
  cellRendererSelector: () => 'Total',
};

const noDetailOrEmptyValue = (context: ReportDetailsColumnsContext, rowData: RowWithMetadata<ScenarioDetailRow>) => {
  if (isEditingMode(context.mode) && getScenarioReportRowDetailId(rowData) === context.editedRowId) {
    return '';
  } else {
    return NoDetailValue;
  }
};

const isPositive = ({ value }: { value: number }) => value > 0;
const isNegative = ({ value }: { value: number }) => value < 0;

const differenceCellClasses = {
  'cell-value-negative': isNegative,
  'cell-value-positive': isPositive,
};

const getTotalColumn = ({
  prefix = '',
  headerName = 'Total',
  numberFormatterOptions,
  calculateTotalStrategy,
}:{
  prefix?: string,
  headerName?: string,
  numberFormatterOptions: UserNumberFormatterOptions,
  scenarioId: number,
  calculateTotalStrategy?: keyof typeof CALCULATE_TOTAL_STRATEGY,
}): ColDef => ({
  colId: `${prefix}TOTAL`,
  headerName,
  cellClass: 'cell-variance',
  cellClassRules: footerCellClassRule,
  valueGetter: (params: Omit<ValueGetterParams, 'data'> & {data: ScenarioDetailRow}) => {
    if (calculateTotalStrategy && calculateTotalStrategy in CALCULATE_TOTAL_STRATEGY) {
      return CALCULATE_TOTAL_STRATEGY[calculateTotalStrategy](params, prefix);
    }
  },
  valueFormatter: customNumberFormatter(numberFormatterOptions),
  ...commonColumnOptions,
});

export const numberFormatterWithNoDetail = (numberFormatterOptions: UserNumberFormatterOptions) => {
  const numberFormatter = customNumberFormatter(numberFormatterOptions);

  return (params: ValueFormatterParams) => {
    if (params.value === NoDetailValue) {
      return NoDetailValue;
    }
    return numberFormatter(params);
  };
};

const noDetailAsZero = (value: number | typeof NoDetailValue) =>
  value === NoDetailValue ? 0 : value;

export const getVarianceColumn = ({
  baseScenarioId,
  compareScenarioId,
  reportColumn,
  numberFormatterOptions,
  context,
}: {
  reportColumn: ReportColDef,
  baseScenarioId: number,
  compareScenarioId: number,
  numberFormatterOptions: UserNumberFormatterOptions,
  context: ReportDetailsColumnsContext,
}): ColDef => ({
  headerName: 'Variance',
  cellClass: 'cell-variance',
  cellClassRules: {
    ...footerCellClassRule,
    ...noDetailCellClassRule,
    ...differenceCellClasses,
  },
  colId: reportColumn.colId,
  valueGetter: ({ data, node, api }) => {
    const { companyCode, countryTaxType } = reportColumn;
    const baseDetailKey = getDetailKey({
      scenarioId: baseScenarioId, companyCode, countryTaxType,
    });
    const compareDetailKey = getDetailKey({
      scenarioId: compareScenarioId, companyCode, countryTaxType,
    });

    if (node?.isRowPinned()) {
      return getDetailsSum(api, `details.${baseDetailKey}.value`)
        - getDetailsSum(api, `details.${compareDetailKey}.value`);
    }

    if (!data.details[baseDetailKey] && !data.details[compareDetailKey]) {
      return noDetailOrEmptyValue(context, data);
    }

    return (data.details[baseDetailKey]?.value || 0) - (data.details[compareDetailKey]?.value || 0);
  },
  valueFormatter: numberFormatterWithNoDetail(numberFormatterOptions),
  ...commonColumnOptions,
});

export const getTotalCompareColumns = ({
  reportGridRow,
  baseScenarioId,
  compareScenarioId,
  numberFormatterOptions,
}: {
  reportGridRow: ScenarioDetailRendererParams,
  baseScenarioId: number,
  compareScenarioId: number,
  numberFormatterOptions: UserNumberFormatterOptions,
}) => ({
  headerName: 'Total',
  children: [
    getTotalColumn({
      headerName: '1st scenario',
      prefix: compareScenariosPrefixes.base,
      scenarioId: baseScenarioId,
      numberFormatterOptions,
      calculateTotalStrategy: reportGridRow.data.calculateTotal,
    }),
    getTotalColumn({
      headerName: '2nd scenario',
      prefix: compareScenariosPrefixes.compare,
      scenarioId: compareScenarioId,
      numberFormatterOptions,
      calculateTotalStrategy: reportGridRow.data.calculateTotal,
    }),
    {
      headerName: 'Variance',
      colId: `${compareScenariosPrefixes.variance}TOTAL`,
      valueGetter: ({ getValue }: ValueGetterParams) => {
        const baseValue = noDetailAsZero(getValue(`${compareScenariosPrefixes.base}TOTAL`));
        const compareValue = noDetailAsZero(getValue(`${compareScenariosPrefixes.compare}TOTAL`));
        return baseValue - compareValue;
      },
      valueFormatter: customNumberFormatter(numberFormatterOptions),
      cellClass: 'cell-variance',
      cellClassRules: {
        ...footerCellClassRule,
        ...differenceCellClasses,
      },
      ...alignRightCellStyle,
      ...commonColumnOptions,
    },
  ],
});


const getDetailDataToUpdate = ({ gridApi, column, node, synchronizeDetails }: {
  gridApi: GridApi,
  column: Column,
  node: IRowNode,
  synchronizeDetails: boolean,
}) => {
  type DetailsColDef = ColDef & { companyCode: string, countryTaxType: string, scenarioId: number };
  const {
    companyCode,
    countryTaxType,
    scenarioId,
  } = column.getUserProvidedColDef() as DetailColumnDefinition;

  if (!node) {
    return [];
  }

  if (!synchronizeDetails) {
    return [{
      key: getDetailKey({
        scenarioId,
        countryTaxType,
        companyCode,
      }),
      data: {
        companyCode,
        countryTaxType,
        scenarioId,
      },
    }];
  }
  return gridApi?.getColumns()?.filter((col: Column) => {
    const colDef = col.getUserProvidedColDef() as DetailsColDef;
    return colDef.companyCode === companyCode && col.isCellEditable(node);
  })
    .map((col: Column) => {
      const { countryTaxType } = col.getUserProvidedColDef() as DetailsColDef;
      return {
        key: getDetailKey({ scenarioId, companyCode, countryTaxType }),
        data: {
          scenarioId,
          companyCode,
          countryTaxType,
        },
      };
    });
};


export const getColumnDefinitions = ({
  baseScenarioId,
  compareScenarioId,
  reportColumns,
  reportGridRow,
  parentGridApi,
  setRowData,
  editRow,
  context,
  numberFormatterOptions,
  showDetailsRemoveModal,
} : {
  baseScenarioId: number,
  reportGridRow: ScenarioDetailRendererParams,
  reportColumns?: (ColDef | ColGroupDef)[],
  compareScenarioId?: number | null,
  parentGridApi: GridApi,
  setRowData: SetRowDataFn<RowWithMetadata<ScenarioDetailRow>>
  editRow: (data: RowWithMetadata<ScenarioDetailRow>, node: IRowNode) => void,
  context: ReportDetailsColumnsContext,
  numberFormatterOptions: UserNumberFormatterOptions,
  showDetailsRemoveModal: OnRemoveDetailsClick,
}): ColDef[] => {
  const createEditableColumn = (
    config: ColumnDefinitionConfig<ScenarioDetailRow, ReportDetailsColumnsContext>
  ) => buildEditableGridColumnDefinitions({
    baseColumnDefinitions: [config],
    setRowData,
    getEntityId: getScenarioReportRowDetailId,
    lookUpData: context,
  })[0];

  /* if total is calculated per company code it means that every editable ctt should have the same detail value,
  otherwise details for different ctt's are independent
   */
  const synchronizeDetails = reportGridRow.data.calculateTotal
    === CALCULATE_TOTAL_TYPES.SUM_PER_COMPANY_CODE;


  const mapColumn = (reportColumn: ColGroupDef | ReportColDef): (ColDef | ColGroupDef) => {
    if (isGroupColumn(reportColumn)) {
      return {
        headerName: reportColumn.headerName,
        headerClass: reportColumn.headerClass,
        children: (reportColumn.children as (ReportColDef | ColGroupDef)[]).map(mapColumn),
      } as ColGroupDef;
    }

    const { countryTaxType, companyCode } = reportColumn;

    const parentColumn = parentGridApi.getColumn(reportColumn.colId as any);
    const parentColumnDef = (parentColumn?.getUserProvidedColDef() as ColDef & ReportColDef);

    const detailKey = getDetailKey({
      scenarioId: reportColumn.isBaseScenario
        ? baseScenarioId
        : compareScenarioId!,
      companyCode, countryTaxType,
    });

    const isCellEditable = !!parentColumn?.isCellEditable(reportGridRow.node);

    return reportColumn.isVarianceColumn && compareScenarioId
      ? getVarianceColumn({
        reportColumn,
        baseScenarioId,
        compareScenarioId,
        numberFormatterOptions,
        context,
      })
      : createEditableColumn({
        baseColumnDefinition: {
          ...pick(reportColumn, fieldsToCopyFromParentColumn),
          field: `details.${detailKey}.value`,
          cellClass: parentColumnDef?.cellClass,
          cellClassRules: {
            ...footerCellClassRule,
            ...noDetailCellClassRule,
          },
          valueFormatter: params => {
            if (params.node?.isRowPinned()) {
              return customNumberFormatter(numberFormatterOptions)(params);
            }
            return numberFormatterWithNoDetail(numberFormatterOptions)(params);
          },
          scenarioId: parentColumnDef?.isBaseScenario ? baseScenarioId : compareScenarioId,
          ...commonColumnOptions,
          ...alignRightCellStyle,
        },
        editableWhen: [GridMode.Add, GridMode.Edit, GridMode.EditAll],
        editableWhenConfig: {
          additionalCondition: () => isCellEditable,
          valueGetter: ({ data, node, api }) => {
            if (node?.isRowPinned()) {
              if (api) {
                return getDetailsSum(api, `details.${detailKey}.value`);
              }
            }
            if (!data.details[detailKey]) {
              return noDetailOrEmptyValue(context, data);
            }

            return data.details[detailKey].value;
          },
          valueParser: floatParser,
          valueSetterFactory: ({ setRowData }) => params => {
            const { data, newValue, column, api, node } = params;

            const detailsToUpdate = getDetailDataToUpdate({
              gridApi: api!,
              column,
              node: node!,
              synchronizeDetails,
            });

            const newDetails = detailsToUpdate!.reduce((newDetails: any, detailToUpdateData: any) => {
              // @ts-ignore
              newDetails[detailToUpdateData.key] = {
                ...detailToUpdateData.data,
                // @ts-ignore
                detailId: data.details[detailToUpdateData.key]?.detailId ?? '',
                reportName: parentColumnDef?.reportName,
                fieldName: getFieldName(reportGridRow.data, parentColumnDef?.reportName),
                value: newValue,
              };

              return newDetails;
            }, {} as ScenarioDetailRow['details']);


            return setRowData({
              ...params,
              field: 'details',
              newValue: {
                ...data.details,
                ...newDetails,
              },
            });
          },
        },
      });
  };

  const dataColumns = (reportColumns as (ReportColDef | ColGroupDef)[]).slice(2).map(mapColumn);

  return [
    {
      ...adminColumnDefinitionFactory({
        editRow: () => null,
        cellRenderer: DetailAdminColumnRenderer,
        context,
        cellRendererParams: () => ({
          editRow,
          removeRow: showDetailsRemoveModal,
        }),
      }),
      pinned: 'left',
    },
    compareScenarioId
      ? getTotalCompareColumns({
        reportGridRow,
        baseScenarioId,
        compareScenarioId,
        numberFormatterOptions,
      })
      : getTotalColumn({
        scenarioId: baseScenarioId,
        numberFormatterOptions,
        calculateTotalStrategy: reportGridRow.data.calculateTotal,
      }),
    ...buildEditableGridColumnDefinitions({
      baseColumnDefinitions: [
        {
          baseColumnDefinition: accountColumnDefinition,
          editableWhen: [GridMode.Add],
          editableWhenConfig: {
            valueSetterFactory: ({ setRowData, lookUpData }) => params => {
              const account = lookUpData.accountsOptions.find(({ value }) => value === params.newValue);
              const firstGroupPost = 1;
              const accountDescriptionRegex = /-(.*)/;
              const description = account?.label.match(accountDescriptionRegex)?.[firstGroupPost]?.trim() ?? '';
              return setRowData({
                field: 'account',
                ...params,
                batchUpdateData: {
                  detailName: description,
                },
                batchUpdateChangeTouched: true,
              });
            },
            valueGetter: ({ node, data }) => {
              if (node?.isRowPinned()) {
                return '';
              }
              return data.account;
            },
          },
          withDropdown: true,
          withDropdownConfig: {
            optionsGetter: context => context.accountsOptions,
          },
        },
        {
          baseColumnDefinition: descriptionColumnDefinition,
          editableWhen: [GridMode.Add],
        },
      ] as ColumnDefinitionConfig<ScenarioDetailRow, ReportDetailsColumnsContext>[],
      getEntityId: getScenarioReportRowDetailId,
      setRowData,
      lookUpData: context,
    }),
    ...dataColumns,
  ];
};
